import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tendersAPI, scrapersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { CompanyBanner } from '../components/PlanGate';
import {
  TrendingUp, AlertTriangle, Clock, CheckCircle,
  FileText, Database, ArrowRight, Zap
} from 'lucide-react';
function StatCard({ title, value, subtitle, icon: Icon, color, dim }) {
  return (
    <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
      <style>{`
  @media (max-width: 768px) {
    .mob-grid-2 { grid-template-columns: repeat(2,1fr) !important; }
    .mob-grid-1 { grid-template-columns: 1fr !important; }
    .mob-stack  { grid-template-columns: 1fr !important; }
    .mob-tabs   { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100% !important; }
    .mob-tabs::-webkit-scrollbar { display: none; }
    .mob-filters { grid-template-columns: 1fr !important; gap: 8px !important; }
  }
`}</style>
      <div style={{
        position: 'absolute', top: '-20px', right: '-20px',
        width: '80px', height: '80px',
        background: dim,
        borderRadius: '50%',
        opacity: 0.5
      }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div style={{
          width: '40px', height: '40px',
          background: dim,
          borderRadius: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Icon size={20} color={color} />
        </div>
      </div>
      <div style={{ fontSize: '32px', fontFamily: 'Syne', fontWeight: '800', marginBottom: '4px', color }}>
        {value}
      </div>
      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>{title}</div>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{subtitle}</div>
    </div>
  );
}

function UrgencySection({ title, count, items, color, dim, icon: Icon }) {
  const navigate = useNavigate();
  if (count === 0) return null;
  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
        <span style={{ fontSize: '14px', fontWeight: '600', color }}>{title}</span>
        <span style={{
          background: dim, color,
          padding: '2px 8px', borderRadius: '100px', fontSize: '12px'
        }}>{count}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.map(tender => (
          <div
            key={tender.id}
            onClick={() => navigate(`/tenders/${tender.id}`)}
            style={{
              background: 'var(--bg-secondary)',
              border: `1px solid ${dim}`,
              borderRadius: '10px',
              padding: '14px 16px',
              cursor: 'pointer',
              transition: 'all 0.15s',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = color}
            onMouseLeave={e => e.currentTarget.style.borderColor = dim}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {tender.title}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                {tender.agency} • {tender.contract_value_formatted}
              </div>
            </div>
            <div style={{ marginLeft: '16px', textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color }}>{tender.days_remaining}d</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>remaining</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pulse, setPulse] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      tendersAPI.deadlinePulse(),
      scrapersAPI.stats()
    ]).then(([pulseRes, statsRes]) => {
      setPulse(pulseRes.data);
      setStats(statsRes.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Loading dashboard...</div>
    </div>
  );

  const totalActive = pulse?.total_active || 0;
  const criticalCount = pulse?.critical?.count || 0;
  const urgentCount = pulse?.urgent?.count || 0;

  return (
    <div>
      {/* Company profile prompt — shown only when no company set up */}
      <CompanyBanner user={user} />

      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ color: 'var(--accent-green)', fontSize: '13px', fontWeight: '600' }}>
            {new Date().toLocaleDateString('en-NG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>
        <h1 style={{ fontSize: '28px', marginBottom: '6px' }}>
          Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'},{' '}
          <span style={{ color: 'var(--accent-green)' }}>{user?.full_name?.split(' ')[0]}</span>
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
          Here's your tender intelligence summary for today.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="mob-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '32px' }}>
        <StatCard
          title="Active Tenders"
          value={stats?.open_tenders || 0}
          subtitle="Open for bidding"
          icon={FileText}
          color="var(--accent-green)"
          dim="var(--accent-green-dim)"
        />
        <StatCard
          title="Critical Deadlines"
          value={criticalCount}
          subtitle="Due within 7 days"
          icon={AlertTriangle}
          color="var(--accent-red)"
          dim="var(--accent-red-dim)"
        />
        <StatCard
          title="Urgent"
          value={urgentCount}
          subtitle="Due within 14 days"
          icon={Clock}
          color="var(--accent-orange)"
          dim="var(--accent-orange-dim)"
        />
        <StatCard
          title="Data Sources"
          value={stats?.active_sources || 0}
          subtitle="Active scrapers"
          icon={Database}
          color="var(--accent-blue)"
          dim="var(--accent-blue-dim)"
        />
      </div>

      <div className="mob-stack" style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '24px' }}>

        {/* Deadline Pulse */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <div>
              <h2 style={{ fontSize: '18px', marginBottom: '4px' }}>Deadline Pulse</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{totalActive} active tenders tracked</p>
            </div>
            <button className="btn btn-ghost" onClick={() => navigate('/tenders')}>
              View All <ArrowRight size={14} />
            </button>
          </div>

          {pulse && (
            <>
              <UrgencySection
                title="CRITICAL — Due this week"
                count={pulse.critical.count}
                items={pulse.critical.items}
                color="var(--accent-red)"
                dim="var(--accent-red-dim)"
              />
              <UrgencySection
                title="URGENT — Due in 2 weeks"
                count={pulse.urgent.count}
                items={pulse.urgent.items}
                color="var(--accent-orange)"
                dim="var(--accent-orange-dim)"
              />
              <UrgencySection
                title="SOON — Due this month"
                count={pulse.soon.count}
                items={pulse.soon.items}
                color="var(--accent-blue)"
                dim="var(--accent-blue-dim)"
              />
              <UrgencySection
                title="NORMAL — Due later"
                count={pulse.normal.count}
                items={pulse.normal.items}
                color="var(--accent-green)"
                dim="var(--accent-green-dim)"
              />
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="card">
            <h3 style={{ fontSize: '15px', marginBottom: '16px' }}>Quick Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { label: 'Browse All Tenders',   icon: FileText,  path: '/tenders',  color: 'var(--accent-green)' },
                { label: 'AI Matched Tenders',    icon: Zap,       path: '/matched',  color: 'var(--accent-orange)' },
                { label: 'Saved Tenders',          icon: CheckCircle, path: '/saved',  color: 'var(--accent-blue)' },
                { label: 'Market Analytics',       icon: TrendingUp, path: '/analytics', color: 'var(--accent-red)' },
              ].map(({ label, icon: Icon, path, color }) => (
                <button
                  key={label}
                  onClick={() => navigate(path)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px', borderRadius: '10px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    cursor: 'pointer', color: 'var(--text-primary)',
                    fontSize: '13px', fontWeight: '500',
                    transition: 'all 0.15s', textAlign: 'left', width: '100%'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.color = color; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                >
                  <Icon size={16} color={color} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="card" style={{ background: 'linear-gradient(135deg, rgba(0,212,161,0.1), rgba(79,142,247,0.05))' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <Zap size={18} color="var(--accent-green)" />
              <span style={{ fontSize: '14px', fontWeight: '700' }}>AI Matching</span>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Update your company profile to get AI-powered tender recommendations.
            </p>
            <button
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', fontSize: '13px' }}
              onClick={() => navigate('/matched')}
            >
              View Matched Tenders
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}